import React, { useState, useEffect, useRef } from 'react';
import axios from 'axios';
import { Copy, Upload, Loader2, Mic, X, Trash2, AlertCircle, CheckCircle } from 'lucide-react';
import { useAuth } from '../../context/AuthContext';
import { toast } from 'sonner';

const VoiceCloning = () => {
  const { token, API } = useAuth();
  const [name, setName] = useState('');
  const [description, setDescription] = useState('');
  const [audioFile, setAudioFile] = useState(null);
  const [audioPreview, setAudioPreview] = useState('');
  const [loading, setLoading] = useState(false);
  const [clones, setClones] = useState([]);
  const [isRecording, setIsRecording] = useState(false);
  const fileRef = useRef(null);
  const mediaRecorderRef = useRef(null);
  const chunksRef = useRef([]);

  useEffect(() => {
    loadClones();
  }, []);

  const loadClones = async () => {
    try {
      const res = await axios.get(`${API}/voice-clone/list`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setClones(res.data.clones || []);
    } catch { /* ignore */ }
  };

  const handleFileSelect = (e) => {
    const file = e.target.files[0];
    if (!file) return;
    if (file.size > 20 * 1024 * 1024) {
      toast.error('File too large. Max 20MB.');
      return;
    }
    setAudioFile(file);
    setAudioPreview(URL.createObjectURL(file));
  };

  const startRecording = async () => {
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      const mediaRecorder = new MediaRecorder(stream);
      mediaRecorderRef.current = mediaRecorder;
      chunksRef.current = [];

      mediaRecorder.ondataavailable = (e) => {
        if (e.data.size > 0) chunksRef.current.push(e.data);
      };

      mediaRecorder.onstop = () => {
        const blob = new Blob(chunksRef.current, { type: 'audio/wav' });
        const file = new File([blob], 'recording.wav', { type: 'audio/wav' });
        setAudioFile(file);
        setAudioPreview(URL.createObjectURL(blob));
        stream.getTracks().forEach(t => t.stop());
      };

      mediaRecorder.start();
      setIsRecording(true);
      toast.success('Recording started. Speak for 10-20 seconds.');
    } catch {
      toast.error('Microphone access denied');
    }
  };

  const stopRecording = () => {
    mediaRecorderRef.current?.stop();
    setIsRecording(false);
  };

  const clearAudio = () => {
    setAudioFile(null);
    setAudioPreview('');
    if (fileRef.current) fileRef.current.value = '';
  };

  const handleCreate = async (e) => {
    e.preventDefault();
    if (!name.trim()) { toast.error('Enter a voice name'); return; }
    if (!audioFile) { toast.error('Upload or record an audio sample'); return; }
    
    setLoading(true);
    try {
      const formData = new FormData();
      formData.append('name', name);
      formData.append('description', description);
      formData.append('audio', audioFile);

      const res = await axios.post(`${API}/voice-clone/create`, formData, {
        headers: {
          Authorization: `Bearer ${token}`,
          'Content-Type': 'multipart/form-data'
        }
      });
      
      setClones(p => [res.data, ...p]);
      toast.success('Voice clone submitted!');
      setName('');
      setDescription('');
      clearAudio();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to create voice clone');
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async (id) => {
    try {
      await axios.delete(`${API}/voice-clone/${id}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setClones(p => p.filter(c => c.id !== id));
      toast.success('Clone deleted');
    } catch {
      toast.error('Failed to delete');
    }
  };

  const statusColors = {
    ready: 'bg-green-500/10 border-green-500/20 text-green-500',
    processing: 'bg-yellow-500/10 border-yellow-500/20 text-yellow-500',
    failed: 'bg-red-500/10 border-red-500/20 text-red-500',
  };

  return (
    <div className="max-w-2xl mx-auto space-y-6">
      <div className="glass-card rounded-2xl p-6 border border-border">
        <div className="flex items-center gap-3 mb-6">
          <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-purple-500 to-pink-600 flex items-center justify-center">
            <Copy className="w-5 h-5 text-white" />
          </div>
          <div>
            <h2 className="font-bold font-outfit text-foreground text-lg">Voice Cloning</h2>
            <p className="text-xs text-muted-foreground">Clone any voice with AI — upload 10-20s of clean audio</p>
          </div>
        </div>

        <form onSubmit={handleCreate} className="space-y-4">
          {/* Voice Name */}
          <div>
            <label className="block text-sm font-medium text-foreground mb-1.5">Voice Name *</label>
            <input
              data-testid="clone-name-input"
              type="text"
              value={name}
              onChange={e => setName(e.target.value)}
              placeholder="My Custom Voice"
              className="w-full px-4 py-3 rounded-xl bg-background border border-border text-foreground placeholder:text-muted-foreground focus:border-primary focus:ring-1 focus:ring-primary text-sm"
            />
          </div>

          {/* Description */}
          <div>
            <label className="block text-sm font-medium text-foreground mb-1.5">Description</label>
            <textarea
              data-testid="clone-desc-input"
              value={description}
              onChange={e => setDescription(e.target.value)}
              placeholder="Describe the voice characteristics..."
              rows={2}
              className="w-full px-4 py-3 rounded-xl bg-background border border-border text-foreground placeholder:text-muted-foreground focus:border-primary focus:ring-1 focus:ring-primary text-sm resize-none"
            />
          </div>

          {/* Audio Upload Area */}
          <div>
            <label className="block text-sm font-medium text-foreground mb-1.5">Audio Sample *</label>
            {!audioFile ? (
              <div className="border-2 border-dashed border-border rounded-xl p-6 text-center hover:border-primary/50 transition-colors">
                <Upload className="w-8 h-8 text-muted-foreground mx-auto mb-3" />
                <p className="text-sm text-muted-foreground mb-3">
                  Upload a clear audio sample (10-20 seconds, single speaker)
                </p>
                <div className="flex gap-3 justify-center">
                  <button
                    type="button"
                    onClick={() => fileRef.current?.click()}
                    data-testid="upload-audio-btn"
                    className="px-4 py-2 rounded-lg bg-secondary text-sm text-foreground hover:bg-secondary/80 transition-colors flex items-center gap-2"
                  >
                    <Upload className="w-4 h-4" /> Upload File
                  </button>
                  <button
                    type="button"
                    onClick={isRecording ? stopRecording : startRecording}
                    data-testid="record-audio-btn"
                    className={`px-4 py-2 rounded-lg text-sm flex items-center gap-2 transition-colors ${
                      isRecording
                        ? 'bg-red-500 text-white hover:bg-red-600'
                        : 'bg-secondary text-foreground hover:bg-secondary/80'
                    }`}
                  >
                    <Mic className="w-4 h-4" /> {isRecording ? 'Stop Recording' : 'Record'}
                  </button>
                </div>
                <input ref={fileRef} type="file" accept="audio/*" className="hidden" onChange={handleFileSelect} />
                {isRecording && (
                  <div className="mt-3 flex items-center justify-center gap-2 text-red-500 text-sm">
                    <div className="w-2 h-2 rounded-full bg-red-500 animate-pulse" />
                    Recording...
                  </div>
                )}
              </div>
            ) : (
              <div className="rounded-xl border border-border p-4 bg-secondary/30">
                <div className="flex items-center justify-between mb-2">
                  <div className="flex items-center gap-2">
                    <Mic className="w-4 h-4 text-primary" />
                    <span className="text-sm text-foreground">{audioFile.name}</span>
                    <span className="text-xs text-muted-foreground">({(audioFile.size / 1024).toFixed(0)} KB)</span>
                  </div>
                  <button type="button" onClick={clearAudio} className="text-muted-foreground hover:text-destructive">
                    <X className="w-4 h-4" />
                  </button>
                </div>
                {audioPreview && (
                  <audio controls src={audioPreview} className="w-full h-8" data-testid="audio-preview" />
                )}
              </div>
            )}
          </div>

          {/* Info */}
          <div className="flex items-start gap-2 text-xs text-muted-foreground p-3 rounded-lg bg-blue-500/5 border border-blue-500/10">
            <AlertCircle className="w-3.5 h-3.5 text-blue-400 flex-shrink-0 mt-0.5" />
            <span>For best results, use a clean recording with minimal background noise. 10-20 seconds of clear speech is ideal.</span>
          </div>

          <button
            data-testid="create-clone-btn"
            type="submit"
            disabled={loading || !name.trim() || !audioFile}
            className="w-full gradient-btn py-3.5 rounded-xl font-semibold text-sm flex items-center justify-center gap-2 disabled:opacity-60"
          >
            {loading ? <><Loader2 className="w-4 h-4 animate-spin" /> Creating Clone...</> : <><Copy className="w-4 h-4" /> Create Voice Clone</>}
          </button>
        </form>
      </div>

      {/* Clones List */}
      {clones.length > 0 && (
        <div className="space-y-3">
          <h3 className="font-semibold font-outfit text-foreground">Your Cloned Voices</h3>
          {clones.map(c => (
            <div key={c.id} className="glass-card rounded-xl p-4 border border-border flex items-center gap-4">
              <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-purple-500 to-pink-600 flex items-center justify-center text-white font-bold">
                {c.name?.[0] || 'C'}
              </div>
              <div className="flex-1 min-w-0">
                <p className="font-medium text-foreground text-sm truncate">{c.name}</p>
                {c.description && <p className="text-xs text-muted-foreground truncate">{c.description}</p>}
              </div>
              <div className={`flex items-center gap-1.5 px-2.5 py-1 rounded-full border text-xs ${statusColors[c.status] || statusColors.processing}`}>
                {c.status === 'ready' && <CheckCircle className="w-3 h-3" />}
                {c.status === 'processing' && <Loader2 className="w-3 h-3 animate-spin" />}
                {c.status === 'failed' && <AlertCircle className="w-3 h-3" />}
                <span className="capitalize">{c.status}</span>
              </div>
              <button
                onClick={() => handleDelete(c.id)}
                className="text-muted-foreground hover:text-destructive transition-colors"
                title="Delete clone"
              >
                <Trash2 className="w-4 h-4" />
              </button>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

export default VoiceCloning;
